/**
 * TanStack Query Client Setup
 * Configures QueryClient with default options and error handling
 */

import { QueryClient } from '@tanstack/react-query'

/**
 * Create and configure QueryClient instance
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Time until data is considered stale
        staleTime: 5 * 60 * 1000, // 5 minutes

        // Time until inactive queries are garbage collected
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)

        // Retry configuration
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors (client errors)
          if (
            error &&
            typeof error === 'object' &&
            'status' in error &&
            typeof error.status === 'number' &&
            error.status >= 400 &&
            error.status < 500
          ) {
            return false
          }

          // Retry up to 3 times for other errors
          return failureCount < 3
        },

        // Retry delay (exponential backoff)
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Refetch on window focus
        refetchOnWindowFocus: true,

        // Refetch on reconnect
        refetchOnReconnect: true,

        // Refetch on mount
        refetchOnMount: true,

        // Network mode
        networkMode: 'online',
      },
      mutations: {
        // Retry configuration for mutations
        retry: (failureCount, error) => {
          // Don't retry mutations on client errors
          if (
            error &&
            typeof error === 'object' &&
            'status' in error &&
            typeof error.status === 'number' &&
            error.status >= 400 &&
            error.status < 500
          ) {
            return false
          }

          // Retry once for other errors
          return failureCount < 1
        },

        // Retry delay
        retryDelay: 1000,

        // Network mode
        networkMode: 'online',
      },
    },
  })
}

/**
 * Get or create QueryClient singleton
 */
let queryClientInstance: QueryClient | null = null

export function getQueryClient(): QueryClient {
  if (!queryClientInstance) {
    queryClientInstance = createQueryClient()
  }

  return queryClientInstance
}
